import axios from 'axios';
import Cookies from 'js-cookie';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('orderstatus_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      Cookies.remove('orderstatus_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Dashboard endpoints
export const dashboardAPI = {
  getKPIs: (branchId) => api.get('/dashboard/kpis', { params: { branch_id: branchId } }),
  getAging: (type, branchId) => api.get('/dashboard/aging', { params: { type, branch_id: branchId } }),
  getRecentActivity: (limit = 20) => api.get('/dashboard/recent-activity', { params: { limit } }),
};

// Branch endpoints
export const branchAPI = {
  getAll: (includeInactive = false) => api.get('/branches', { params: { include_inactive: includeInactive } }),
  get: (id) => api.get(`/branches/${id}`),
  create: (data) => api.post('/branches', data),
  getChildren: (id) => api.get(`/branches/${id}/children`),
};

// User endpoints
export const userAPI = {
  getAll: (branchId, role) => api.get('/users', { params: { branch_id: branchId, role } }),
  get: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
};

// Supplier endpoints
export const supplierAPI = {
  getAll: (search, companyGroup, vendorType) => api.get('/suppliers', { 
    params: { search, company_group: companyGroup, vendor_type: vendorType } 
  }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Part catalog endpoints
export const partAPI = {
  getAll: (search, category) => api.get('/parts', { params: { search, category } }),
  get: (id) => api.get(`/parts/${id}`),
  create: (data) => api.post('/parts', data),
};

// Storage location endpoints
export const storageAPI = {
  getAll: (branchId) => api.get('/storage-locations', { params: { branch_id: branchId } }),
  create: (data) => api.post('/storage-locations', data),
};

// Part request endpoints
export const requestAPI = {
  getAll: (params) => api.get('/requests', { params }),
  get: (id) => api.get(`/requests/${id}`),
  create: (data) => api.post('/requests', data),
  approve: (id) => api.put(`/requests/${id}/approve`),
  reject: (id, reason) => api.put(`/requests/${id}/reject`, { reason }),
};

// Approval queue endpoints
export const approvalAPI = {
  getPending: (branchId) => api.get('/approvals/pending', { params: { branch_id: branchId } }),
};

// Purchase order endpoints (legacy)
export const purchaseOrderAPI = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  get: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  updateStatus: (id, status, tracking, reference) => 
    api.put(`/purchase-orders/${id}/status`, { status, tracking_number: tracking, supplier_reference: reference }),
};

// Drivers endpoints
export const driverAPI = {
  getAll: () => api.get('/drivers'),
  create: (data) => api.post('/drivers', data),
  delete: (id) => api.delete(`/drivers/${id}`),
};

// Units endpoints
export const unitAPI = {
  getAll: () => api.get('/units'),
  create: (data) => api.post('/units', data),
  delete: (id) => api.delete(`/units/${id}`),
};

// Pickup orders endpoints
export const pickupOrderAPI = {
  getAll: (params) => api.get('/pickup-orders', { params }),
  get: (id) => api.get(`/pickup-orders/${id}`),
  create: (data) => api.post('/pickup-orders', data),
  updateStatus: (id, status, notes) => api.put(`/pickup-orders/${id}/status`, { status, notes }),
  delete: (id) => api.delete(`/pickup-orders/${id}`),
};

// Receiving endpoints
export const receivingAPI = {
  getPending: (branchId) => api.get('/receiving/pending', { params: { branch_id: branchId } }),
  receive: (data) => api.post('/receiving/receive', data),
};

// Part instance endpoints
export const partInstanceAPI = {
  getAll: (params) => api.get('/part-instances', { params }),
  get: (id) => api.get(`/part-instances/${id}`),
  getByBarcode: (barcode) => api.get(`/part-instances/barcode/${barcode}`),
  transferCustody: (data) => api.post('/custody-transfers', data),
};

// Notification endpoints
export const notificationAPI = {
  getAll: (unreadOnly = false, limit = 50) => api.get('/notifications', { params: { unread_only: unreadOnly, limit } }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

export default api;
